import React, { ReactNode, useEffect, useState } from 'react'
import { ActivityIndicator, Platform, View } from 'react-native'
import { usePathname, useRootNavigationState, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'

import { AuthContext, initStoreAndLoad } from '@/stores/auth'
import store from '@/stores/store'
import { SHARE_LOGOUT_STORAGE_KEY, webStore } from '@/stores/webStore'
import { useToast } from '@/components/base'

// TODO: Probably should rename this
export function NavigationManager({ children }: { children: ReactNode }) {
  const auth = AuthContext.useActorRef()
  const router = useRouter()
  const toast = useToast()
  const { t } = useTranslation()
  const [isAuthReady, setIsAuthReady] = useState(false)
  const loggedIn = AuthContext.useSelector((snapshot) => snapshot.matches('LOGGED_IN'))
  const errorLoggingOut = AuthContext.useSelector((snapshot) => snapshot.matches('ERROR_LOGGING_OUT'))
  const loggingOut = AuthContext.useSelector((snapshot) => snapshot.matches('LOGGING_OUT'))
  useEffect(() => {
    const subscription = auth.subscribe((snapshot) => {
      if (snapshot.matches('LOGGED_IN') || snapshot.matches('LOGGED_OUT')) {
        setIsAuthReady(true)
      }
    })

    // NavigationManager can remount while its provider stays mounted. In that
    // case, the actor has already validated this session and does not need a
    // second bootstrap transition.
    if (auth.getSnapshot().matches('LOGGED_IN')) {
      setIsAuthReady(true)
      return subscription.unsubscribe
    }

    setIsAuthReady(false)
    if (Platform.OS === 'web') {
      initStoreAndLoad(webStore)
    } else {
      initStoreAndLoad(store)
    }
    auth.send({ type: 'LOAD_AUTH' })

    return subscription.unsubscribe
  }, [auth])

  useEffect(() => {
    if (errorLoggingOut) {
      console.log('error logging out')
      // TODO: unhardcode this
      toast.show({ variant: 'error', description: t('auth:loginExpired') })
    } else if (loggingOut) {
      console.log('logging out')
      router.replace('/landing')
    }
  }, [errorLoggingOut, loggingOut, router, toast])

  useEffect(() => {
    if (Platform.OS !== 'web') return

    const onStorage = (event: StorageEvent) => {
      if (event.key === SHARE_LOGOUT_STORAGE_KEY && event.newValue) {
        auth.send({ type: 'SESSION_REVOKED' })
      }
    }

    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [auth])

  // Protected screens issue GraphQL queries as soon as they mount. Keep the
  // navigator's children unmounted until the persisted session has finished
  // hydrating, otherwise a fresh load can send those queries without a token.
  if (!isAuthReady) {
    return (
      <View className={'bg-background flex-1 items-center justify-center'}>
        <ActivityIndicator />
      </View>
    )
  }

  return (
    <>
      <RouteGuard isAuthReady={isAuthReady} loggedIn={loggedIn} />
      {children}
    </>
  )
}

function RouteGuard({ isAuthReady, loggedIn }: { isAuthReady: boolean; loggedIn: boolean }) {
  const pathname = usePathname()
  const rootNavigationState = useRootNavigationState()
  const router = useRouter()

  useEffect(() => {
    if (!rootNavigationState?.key || !isAuthReady) return

    if (pathname === '/') {
      router.replace(loggedIn ? '/home' : '/landing')
      return
    }

    if (!loggedIn && !isNonProtectedRoute(pathname)) {
      router.replace('/landing')
      return
    }

    if (
      loggedIn &&
      isNonProtectedRoute(pathname) &&
      !SPECIAL_ROUTES.includes(pathname) &&
      !SELF_ROUTING_AUTH_ROUTES.includes(pathname)
    ) {
      router.replace('/home')
    }
  }, [isAuthReady, loggedIn, pathname, rootNavigationState, router])

  return null
}

// hacking payment path for now, since getting globalSearchParams causes infinite rendering
const SPECIAL_ROUTES = ['/payment', '/scan2pay-qr', '/connect-app']
// Login and registration own their successful destination because login can carry a redirect.
// Routing them here as well would dispatch two native stack replacements for the same auth transition.
const SELF_ROUTING_AUTH_ROUTES = ['/login', '/register']
const NON_PROTECTED_ROUTES = ['/landing', '/login', '/register', '/forgot-password', ...SPECIAL_ROUTES]
const isNonProtectedRoute = (pathname: string) => {
  if (NON_PROTECTED_ROUTES.includes(pathname)) {
    return true
  }
}
